// ContactSubmissionsContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const ContactSubmissionsContext = createContext();

export const useContactSubmissions = () => {
  const context = useContext(ContactSubmissionsContext);
  if (!context) {
    throw new Error('useContactSubmissions must be used within a ContactSubmissionsProvider');
  }
  return context;
};

export const ContactSubmissionsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for contact submissions endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getSubmissionsBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/contact-submissions/api/v1/contact-submissions`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/contact-submissions/api/v1/contact-submissions`;
    } else {
      throw new Error('Invalid login type for contact submissions access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access contact submissions');
    }
  }, [loginType]);

  // Get all contact submissions with pagination and filters
  const getSubmissions = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.resolved !== undefined) queryParams.append('resolved', options.resolved);
      if (options.adminId) queryParams.append('admin_id', options.adminId);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getSubmissionsBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch contact submissions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get submission by ID
  const getSubmissionById = useCallback(async (submissionId) => {
    validateAccess();
    
    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(submissionId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get contact submission by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Update resolution fields (PATCH)
  const updateResolution = useCallback(async (submissionId, updateData) => {
    validateAccess();
    
    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(submissionId)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update contact submission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Mark submission as resolved
  const markAsResolved = useCallback(async (submissionId, resolution, adminId) => {
    validateAccess();
    
    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    if (!adminId) {
      throw new Error('Admin ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(submissionId)}/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resolution: resolution || '',
            admin_id: adminId,
          }),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to mark submission as resolved';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Mark submission as unresolved
  const markAsUnresolved = useCallback(async (submissionId) => {
    validateAccess();
    
    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(submissionId)}/unresolve`,
        {
          method: 'POST',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to mark submission as unresolved';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Delete submission
  const deleteSubmission = useCallback(async (submissionId) => {
    validateAccess();
    
    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(submissionId)}`,
        {
          method: 'DELETE',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete contact submission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get submission statistics
  const getStats = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submission statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get unresolved submissions
  const getUnresolvedSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, resolved: false });
  }, [getSubmissions, validateAccess]);

  // Get resolved submissions
  const getResolvedSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, resolved: true });
  }, [getSubmissions, validateAccess]);

  // Get submissions by admin ID (who resolved them)
  const getSubmissionsByAdminId = useCallback(async (adminId, options = {}) => {
    validateAccess();
    
    if (!adminId) {
      throw new Error('Admin ID is required');
    }

    return await getSubmissions({ ...options, adminId });
  }, [getSubmissions, validateAccess]);

  // Get submissions by date range
  const getSubmissionsByDateRange = useCallback(async (dateFrom, dateTo, options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, dateFrom, dateTo });
  }, [getSubmissions, validateAccess]);

  // Search submissions
  const searchSubmissions = useCallback(async (searchTerm, options = {}) => {
    validateAccess();
    
    if (!searchTerm || searchTerm.length < 2) {
      throw new Error('Search term must be at least 2 characters');
    }

    return await getSubmissions({ ...options, search: searchTerm });
  }, [getSubmissions, validateAccess]);

  // Get recent submissions
  const getRecentSubmissions = useCallback(async (limit = 10) => {
    validateAccess();
    return await getSubmissions({ limit, offset: 0 });
  }, [getSubmissions, validateAccess]);

  // Get today's submissions
  const getTodaySubmissions = useCallback(async (options = {}) => {
    validateAccess();
    
    const today = new Date().toISOString().split('T')[0];
    return await getSubmissions({ ...options, dateFrom: today, dateTo: today });
  }, [getSubmissions, validateAccess]);

  // Get this month's submissions
  const getThisMonthSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    return await getSubmissions({ ...options, dateFrom: firstDay, dateTo: lastDay });
  }, [getSubmissions, validateAccess]);

  // Export submission data
  const exportSubmissionData = useCallback(async (filters = {}) => {
    validateAccess();

    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0,
    };

    const result = await getSubmissions(exportOptions);
    
    const exportData = result.submissions.map(submission => ({
      'Submission ID': submission.submission_id,
      'Name': submission.name,
      'Email': submission.email,
      'Phone': submission.phone || '',
      'Location': submission.location || '',
      'Contact Reason': submission.contact_reason || '',
      'Date': submission.date,
      'Time': submission.time,
      'Resolved': submission.resolved ? 'Yes' : 'No',
      'Resolution': submission.resolution || '',
      'Resolved By': submission.admin_id || '',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      filename: `contact_submissions_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getSubmissions, validateAccess]);

  // Get submission summary
  const getSubmissionSummary = useCallback(async () => {
    validateAccess();

    try {
      const [stats, recentSubmissions] = await Promise.all([
        getStats(),
        getRecentSubmissions(10),
      ]);

      return {
        totalSubmissions: stats.stats.total_submissions,
        resolvedCount: stats.stats.resolved_count,
        unresolvedCount: stats.stats.unresolved_count,
        submissionsToday: stats.stats.submissions_today,
        submissionsThisMonth: stats.stats.submissions_this_month,
        recentSubmissions: recentSubmissions.submissions,
        accessLevel: loginType,
        hasSubmissions: stats.stats.total_submissions > 0,
        resolutionRate: stats.stats.total_submissions > 0 
          ? ((stats.stats.resolved_count / stats.stats.total_submissions) * 100).toFixed(1)
          : 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submission summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getStats, getRecentSubmissions, validateAccess, loginType]);

  // Health check
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/contact-submissions/health`
        : `${API_BASE_URL}/admin/contact-submissions/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateSubmissionFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && 
        new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Validate resolution input
  const validateResolutionInput = useCallback((resolutionData) => {
    const errors = [];

    if (!resolutionData.admin_id || resolutionData.admin_id.trim() === '') {
      errors.push({ field: 'admin_id', message: 'Admin ID is required' });
    }

    return errors;
  }, []);

  // Format submission data for display
  const formatSubmissionData = useCallback((submission) => {
    const getStatusColor = (resolved) => {
      return resolved ? 'green' : 'orange';
    };

    const getStatusIcon = (resolved) => {
      return resolved ? '✅' : '⏳';
    };

    return {
      ...submission,
      statusColor: getStatusColor(submission.resolved),
      statusIcon: getStatusIcon(submission.resolved),
      statusText: submission.resolved ? 'Resolved' : 'Pending',
      formattedDate: submission.date 
        ? new Date(submission.date).toLocaleDateString() 
        : 'N/A',
      formattedTime: submission.time || 'N/A',
      formattedDateTime: submission.date && submission.time
        ? `${new Date(submission.date).toLocaleDateString()} ${submission.time}`
        : 'N/A',
      hasPhone: !!submission.phone && submission.phone.trim() !== '',
      hasLocation: !!submission.location && submission.location.trim() !== '',
      hasContactReason: !!submission.contact_reason && submission.contact_reason.trim() !== '',
      hasResolution: !!submission.resolution && submission.resolution.trim() !== '',
    };
  }, []);

  // Get endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/contact-submissions/api/v1/contact-submissions`
        : `${API_BASE_URL}/admin/contact-submissions/api/v1/contact-submissions`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core CRUD Actions
    getSubmissions,
    getSubmissionById,
    updateResolution,
    markAsResolved,
    markAsUnresolved,
    deleteSubmission, // Added here
    
    // Statistics Actions
    getStats,
    
    // Filter Actions
    getUnresolvedSubmissions,
    getResolvedSubmissions,
    getSubmissionsByAdminId,
    getSubmissionsByDateRange,
    searchSubmissions,
    getRecentSubmissions,
    getTodaySubmissions,
    getThisMonthSubmissions,
    
    // Utility Actions
    exportSubmissionData,
    getSubmissionSummary,
    checkHealth,
    
    // Helpers
    clearError,
    validateSubmissionFilters,
    validateResolutionInput,
    formatSubmissionData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasSubmissionAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <ContactSubmissionsContext.Provider value={value}>
      {children}
    </ContactSubmissionsContext.Provider>
  );
};